import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import MaterialTable from '@material-table/core';
import flowRight from 'lodash.flowright';

import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import { actions as reduxActions } from '../reducers';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import tableColumns from './tableColumns';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import CustomTableTitle from '../../../partials/CustomTableTitle';
import { translate } from '../../../i18n/I18nProvider';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import getAccounts from '../actions/getAccounts';
import AccountDetailView from './AccountDetailView';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';

const AccountsTable = ({
  accounts,
  columns,
  setColumns,
  getAccounts,
  isLoading,
  clientId
}) => {
  const [filtering, setFiltering] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { localization, language } = useTableLocalization();

  useEffect(() => {
    getAccounts(clientId);
    // eslint-disable-next-line
  }, [clientId]);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns();
  }, []);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'Accounts-1',
    setColumns,
    tableDefaultColumns,
    language
  });

  useEffect(() => {
    if (refresh) {
      getAccounts(clientId);
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const actions = useMemo(
    () => [
      {
        icon: () => <FilterButton />,
        onClick: () => {
          setFiltering(!filtering);
        },
        isFreeAction: true,
        tooltip: translate('BUTTON.FILTER')
      },
      {
        icon: () => <RefreshButton tableHeader={true} />,
        onClick: () => setRefresh(true),
        isFreeAction: true
      }
    ],
    // eslint-disable-next-line
    [filtering]
  );

  const options = useMemo(() => {
    return {
      ...mapTableOptions({ exportData: false }),
      selection: false,
      filtering,
      pageSize
    };
    // eslint-disable-next-line
  }, [filtering, pageSize]);

  const detailPanel = useCallback(
    ({ rowData }) => (
      <AccountDetailView account={rowData} clientId={clientId} />
    ),
    [clientId]
  );

  return (
    <>
      <MaterialTable
        title={
          <CustomTableTitle
            text="MENU.ACCOUNTS"
            isLoading={isLoading}
            icon={<i className="fas fa-book"></i>}
          />
        }
        icons={customTableIcons}
        actions={actions}
        columns={columns}
        data={accounts}
        options={options}
        localization={localization}
        detailPanel={detailPanel}
        onChangeColumnHidden={onChangeColumnHidden}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
};

const mapStatesToProps = ({ accounts, loader, selectClient }) => {
  return {
    ...accounts,
    isLoading: loader.isSpinVisible,
    clientId: Number(selectClient.selectedClient.id)
  };
};

const mapDispatchToProps = {
  getAccounts,
  setColumns: reduxActions.setColumns
};

export default flowRight(connect(mapStatesToProps, mapDispatchToProps))(
  AccountsTable
);
